import {messages} from '@smartmixin/i18n';
import React from 'react';
import {useIntl} from 'react-intl';
import type {Location} from '../api/types';
import {MyWeather} from '../utils';
import useMyWeatherSearch from './useMyWeatherSearch';

type Position = {latitude: number; longitude: number};

/**
 * The My weather search field's state (FE #381 step 2), in both apps: the
 * text typed, and the search it drives. Stations come nearest first from
 * the GPS entry (`userLocation`), when the app knows it.
 *
 * Its ⌖ lists the stations around a fix instead (`onNearby`), the field
 * then reading "My location": typing replaces it (`MyWeather.editOfLabel`),
 * deleting from it clears. A fix landing once the field was edited (typed
 * in, cleared) is dropped.
 */
const useMyWeatherSearchField = (userLocation: Location | null) => {
  const {formatMessage} = useIntl();
  const latitude = userLocation?.coordinates.latitude;
  const longitude = userLocation?.coordinates.longitude;
  const position = React.useMemo(
    () =>
      latitude != null && longitude != null ? {latitude, longitude} : null,
    [latitude, longitude],
  );
  const search = useMyWeatherSearch(position);
  const [text, setText] = React.useState('');
  const nearbyText = formatMessage(messages.stations.myLocation);
  // The field's edits (typing, Clear), counted. A fix can land 30 s after
  // the ⌖ asked for it, and the field may be empty again by then (typed,
  // then cleared): the user has moved on all the same. `onNearby` holds
  // the count it was made under, and the ⌖ hands a fix to the handler of
  // its latest press (each app locates in its own way).
  const [edits, setEdits] = React.useState(0);
  const editsNow = React.useRef(0);
  const countEdit = React.useCallback(() => {
    editsNow.current += 1;
    setEdits(editsNow.current);
  }, []);

  const onClear = React.useCallback(() => {
    countEdit();
    setText('');
    search.clear();
  }, [countEdit, search]);

  const onChangeText = React.useCallback(
    (value: string) => {
      let typed = value;
      // "My location" stands for the ⌖'s list, not for a name.
      if (search.nearby) {
        if (value === nearbyText) {
          return; // still the label: nothing to search
        }
        const edited = MyWeather.editOfLabel(nearbyText, value);
        if (edited === null) {
          onClear();
          return;
        }
        typed = edited;
      }
      countEdit();
      setText(typed);
      search.search(typed);
    },
    [countEdit, nearbyText, onClear, search],
  );

  const onNearby = React.useCallback(
    (around: Position) => {
      // Edited since the ⌖ was pressed: what was typed meanwhile wins.
      if (edits !== editsNow.current) {
        return;
      }
      setText(nearbyText);
      search.searchNearby(around);
    },
    [edits, nearbyText, search],
  );

  return {
    text,
    onChangeText,
    onClear,
    onNearby,
    nearby: search.nearby,
    search,
  };
};

export default useMyWeatherSearchField;
